import type {AccountLastContactedTimeFragment, AccountPageFragment} from 'nutshell-graphql-types';
import {HeaderFieldType} from 'nutshell-graphql-types';

import {EditFieldsWrapper} from '../../shared/header/edit-fields/edit-fields-wrapper';
import {PeepLastContacted} from '../../shared/header/edit-fields/peep-last-contacted';
import {PeepTerritory} from '../../shared/header/edit-fields/peep-territory';

import {AccountEmployeeCountField} from './account-employee-count-field';
import {AccountIndustryField} from './account-industry-field';
import {AccountRevenueField} from './account-revenue-field';
import {AccountTypeField} from './account-type-field';

interface Props {
    account: AccountPageFragment;
    accountLastContact: AccountLastContactedTimeFragment | null;
    isLoadingAccountLastContact: boolean;
    canAccessSetup: boolean;
    onUpdateAccountTerritory: (
        territoryId: string | null,
        territoryName: string | null
    ) => Promise<any>;
    onUpdateAccountType: (accountTypeId: string, accountTypeName: string) => Promise<any>;
    onUpdateAccountIndustry: (
        industryId: string | null,
        industryName: string | null
    ) => Promise<any>;
    onUpdateAccountRevenue: (revenue: number) => Promise<any>;
    onUpdateAccountEmployeeCount: (employeeCount: number) => Promise<any>;
}

export function AccountEditFields(props: Props) {
    const headerFields = props.account.headerFields;

    const enabledFields = new Set<HeaderFieldType>(
        headerFields.filter((f) => f.isEnabled).map((f) => f.type)
    );

    return (
        <EditFieldsWrapper>
            {enabledFields.has(HeaderFieldType.COMPANY_TYPE) && (
                <AccountTypeField
                    accountTypeId={props.account.accountType?.id ?? null}
                    onUpdateAccountType={props.onUpdateAccountType}
                    canAccessSetup={props.canAccessSetup}
                />
            )}
            {enabledFields.has(HeaderFieldType.INDUSTRY) && (
                <AccountIndustryField
                    industryId={props.account.industry?.id}
                    onUpdateAccountIndustry={props.onUpdateAccountIndustry}
                    canAccessSetup={props.canAccessSetup}
                />
            )}
            {enabledFields.has(HeaderFieldType.LAST_CONTACTED) && (
                <PeepLastContacted
                    entityType='account'
                    lastContactedTime={props.accountLastContact?.lastContactedTime ?? null}
                    myLastContactedTime={props.accountLastContact?.myLastContactedTime ?? null}
                    isLoadingPeepLastContact={props.isLoadingAccountLastContact}
                />
            )}
            {enabledFields.has(HeaderFieldType.TERRITORY) && (
                <PeepTerritory
                    territoryId={props.account.territory?.id}
                    onUpdatePeepTerritory={(
                        territoryId: string | null,
                        territoryName: string | null
                    ) => {
                        props.onUpdateAccountTerritory(territoryId, territoryName);
                    }}
                    canAccessSetup={props.canAccessSetup}
                />
            )}
            {enabledFields.has(HeaderFieldType.NUMBER_OF_EMPLOYEES) && (
                <AccountEmployeeCountField
                    employeeCount={props.account.employeeCount}
                    onUpdateAccountEmployeeCount={props.onUpdateAccountEmployeeCount}
                />
            )}
            {enabledFields.has(HeaderFieldType.REVENUE) && (
                <AccountRevenueField
                    revenue={props.account.revenue?.currencyValue.amount}
                    onUpdateAccountRevenue={props.onUpdateAccountRevenue}
                />
            )}
        </EditFieldsWrapper>
    );
}
